export type ActivationState = {
  orgToken: string;
  isActive: boolean;
  version: number;
  epoch: string | null;
  updatedAt: number;
};

export const ACTIVATION_STATE_CACHE_KEY = 'tc.activationState';

export type ActivationStateListener = (state: ActivationState) => void;

const listeners = new Set<ActivationStateListener>();

export function subscribeToActivationState(listener: ActivationStateListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function readStorage(key: string): unknown {
  try {
    const raw = localStorage.getItem(key);
    return raw === null ? undefined : JSON.parse(raw);
  } catch {
    return undefined;
  }
}

function writeStorage(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {}
}

export function readHeldStates(): Record<string, ActivationState> {
  const parsed = readStorage(ACTIVATION_STATE_CACHE_KEY);
  if (
    parsed === undefined ||
    parsed === null ||
    typeof parsed !== 'object' ||
    Array.isArray(parsed)
  ) {
    return {};
  }
  return parsed as Record<string, ActivationState>;
}

export function heldState(orgToken: string): ActivationState | undefined {
  return readHeldStates()[orgToken];
}

function supersedes(incoming: ActivationState, held: ActivationState | undefined): boolean {
  if (!held || held.version === 0) return true; // row 1

  if (held.epoch === null && incoming.epoch !== null) {
    return incoming.version >= held.version; // row 2
  }

  if (held.epoch === null && incoming.epoch === null) {
    return incoming.version > held.version; // row 3
  }

  if (held.epoch !== null && incoming.epoch === held.epoch) {
    return incoming.version > held.version; // rows 4 & 5
  }

  if (held.epoch !== null && incoming.epoch === null) {
    return false; // row 6
  }

  return false; // row 7: differing non-null epochs
}

export function applyState(incoming: ActivationState): boolean {
  const all = readHeldStates();
  const held = all[incoming.orgToken];

  if (!supersedes(incoming, held)) return false;

  writeStorage(ACTIVATION_STATE_CACHE_KEY, { ...all, [incoming.orgToken]: incoming });
  listeners.forEach((listener) => listener(incoming));
  return true;
}

export function fromWire(orgToken: string, wire: unknown): ActivationState | undefined {
  if (typeof wire !== 'object' || wire === null) return undefined;

  const record = wire as Record<string, unknown>;
  if (record.state !== 'on' && record.state !== 'off') return undefined;
  if (typeof record.version !== 'number') return undefined;

  const epoch = record.epoch;
  const updatedAt = record.updated_at;

  return {
    orgToken,
    isActive: record.state === 'on',
    version: record.version,
    epoch: typeof epoch === 'string' ? epoch : null,
    updatedAt: typeof updatedAt === 'number' ? updatedAt : 0,
  };
}

export function etagFor(state: ActivationState | undefined): string | undefined {
  if (!state) return undefined;
  return `${state.epoch ?? '0'}:${state.version}`;
}
